import { ChangeEvent, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { InputStyle, ValidationErrorStyle } from "src/common";
import { InputLabel } from "src/components";
import { colors } from "src/theme";

const Box = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
})

const OptionsList = styled.div({
  width: '100%',
  boxSizing: 'border-box' as const,
  outline: 'none',
  display: 'block',
  padding: '9px 12px',
  fontSize: '18px',
  fontWeight: 400,
  fontStyle: 'normal',
  lineHeight: '24px',
  color: colors.text1,
  borderRadius: '8px',
  border: `1px solid ${colors.border5}`,
  transition: 'border-color .15s ease-in-out, box-shadow .15s ease-in-out',
  backgroundColor: colors.background7,
  maxHeight: '150px',
  overflowY: 'auto' as const,
  zIndex: 99,
});

const OptionItem = styled.div({
  cursor: 'pointer',
  padding: '4px 0',

  '&:hover': {
    backgroundColor: colors.background6,
  }
})

interface SelectOption {
  value: string;
  label: string;
  contains?: string;
}

interface SelectProps {
  id?: string;
  label?: string;
  value?: string;
  error?: string;
  disabled?: boolean;
  options: SelectOption[];
  onChange?: (value: string) => void;
}

export const Select: React.FC<SelectProps> = ({
  id,
  label,
  value,
  error,
  disabled,
  options,
  onChange
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);

  useEffect(() => {
    if (value) {
      const selectedOption = options.find(opt => opt.value === value);
      if (selectedOption) {
        setInputValue(selectedOption.label);
      }
    }
  }, [value, options]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowOptions(true);

    // Filtriranje opcija prema unosu
    const filtered = options.filter(opt => 
      opt.label.toLowerCase().includes(newValue.toLowerCase()) || 
      (opt.contains && opt.contains.toLowerCase().includes(newValue.toLowerCase()))
    );
    setFilteredOptions(filtered);
  };

  const handleOptionClick = (option: SelectOption) => {
    setInputValue(option.label);
    setShowOptions(false);
    if (onChange) {
      onChange(option.value);
    }
  };

  return (
    <Box>
      <div>
        {label ? <InputLabel htmlFor={id} disabled={disabled}>{label}</InputLabel> : null}
        <InputStyle
          id={id}
          type='text'
          value={inputValue}
          disabled={disabled}
          onChange={handleChange}
          onFocus={() => setShowOptions(true)}
          hasError={!!error}
          autoComplete="off"
        />
        {error ? <ValidationErrorStyle>{error}</ValidationErrorStyle> : null}
      </div>
      {showOptions && filteredOptions.length > 0 && (
        <OptionsList>
          {filteredOptions.map(option => (
            <OptionItem 
              key={option.value} 
              onClick={() => handleOptionClick(option)}
            >
              {option.label}
            </OptionItem>
          ))}
        </OptionsList>
      )}
    </Box>
  );
};